import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Lock, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { createCheckoutSession } from '@/utils/stripe';

const PaywallModal = ({ isOpen, onClose, botName = 'this bot' }) => {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    await createCheckoutSession('pro');
    // User will be redirected to Stripe, so no need to setLoading(false)
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="paygbot-modal-shell bg-white rounded-2xl shadow-2xl max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="paywall-close-button"
          >
            <X size={24} weight="bold" />
          </button>
          
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Lock size={32} weight="duotone" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Unlock Full PayGBot Access</h2>
              <p className="text-blue-100 text-lg">
                Upgrade to run all bots, save your projects, and build your online income faster.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-900">
              <span className="font-semibold">You're trying to access {botName}.</span> This bot is available on paid plans.
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 text-lg">What you'll get with Pro:</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">Access all 4 bots</div>
                  <p className="text-sm text-slate-600">Content, Growth, Money, and Business Builder</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">Unlimited bot runs</div>
                  <p className="text-sm text-slate-600">Run as many projects as you need</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">Save all your results</div>
                  <p className="text-sm text-slate-600">Keep everything in your project library</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">Full PayGBot system</div>
                  <p className="text-sm text-slate-600">Everything you need to build online income</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Highlight */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center">
            <div className="text-sm text-blue-600 font-semibold uppercase tracking-wide mb-2">Most Popular</div>
            <div className="text-4xl font-bold text-slate-900 mb-1">$79<span className="text-xl text-slate-600">/mo</span></div>
            <p className="text-slate-600 text-sm">Pro Plan - Everything you need</p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleUpgrade}
              disabled={loading}
              data-testid="paywall-upgrade-button"
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              <span>{loading ? 'Loading...' : 'Upgrade Now'}</span>
              {!loading && <ArrowRight size={20} weight="bold" />}
            </button>
            <Link
              to="/pricing"
              className="flex-1 px-6 py-4 bg-white hover:bg-slate-50 text-slate-700 font-medium border-2 border-slate-200 rounded-lg transition-colors text-center"
            >
              View All Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
